import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, UserPlus, CalendarClock, Plane, PoundSterling, X, Loader2, Check, Ban, Link2, Unlink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Strip empty-string / null optional fields so backend Optional[...] validators
// (e.g. EmailStr) accept the omission instead of rejecting "".
const clean = (obj) => Object.fromEntries(
  Object.entries(obj).filter(([, v]) => v !== '' && v != null)
);

const useOrgScopedHeaders = (token) => {
  const [orgId, setOrgId] = useState(() => {
    try { return localStorage.getItem('current_organization_id') || null; } catch { return null; }
  });
  const [locationId, setLocationId] = useState(() => {
    try { return localStorage.getItem('current_location_id') || null; } catch { return null; }
  });
  useEffect(() => {
    const refresh = () => {
      try { setLocationId(localStorage.getItem('current_location_id') || null); } catch {}
    };
    window.addEventListener('storage', refresh);
    window.addEventListener('locationChanged', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('locationChanged', refresh);
    };
  }, []);
  useEffect(() => {
    if (!token || orgId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/platform/context`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(ctx => {
        if (cancelled || !ctx) return;
        const id = ctx?.organizations?.[0]?.id;
        if (id) { setOrgId(id); try { localStorage.setItem('current_organization_id', id); } catch {} }
      }).catch(() => {});
    return () => { cancelled = true; };
  }, [token, orgId]);
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(orgId ? { 'X-Organization-Id': orgId } : {}),
    ...(locationId ? { 'X-Location-Id': locationId } : {}),
  }), [token, orgId, locationId]);
  return { headers, orgId, locationId };
};

const StatCard = ({ label, value, icon: Icon }) => (
  <div className="bg-white rounded-xl p-5 shadow-sm border border-surface-border" data-testid={`hrm-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
        <Icon className="w-5 h-5 text-primary-500" />
      </div>
      <div>
        <div className="text-2xl font-display text-ink tabular-nums">{value}</div>
        <div className="text-xs text-ink-muted">{label}</div>
      </div>
    </div>
  </div>
);

const TABS = ['Team', 'Rota', 'Time-off'];

const HRM = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const { headers, orgId, locationId } = useOrgScopedHeaders(token);

  const [tab, setTab] = useState('Team');
  const [employees, setEmployees] = useState([]);
  const [accountCandidates, setAccountCandidates] = useState([]);
  const [accountSelections, setAccountSelections] = useState({});
  const [shifts, setShifts] = useState([]);
  const [timeoff, setTimeoff] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [empForm, setEmpForm] = useState(null);
  const [shiftForm, setShiftForm] = useState(null);
  const [timeoffForm, setTimeoffForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const showAccountControls = accountCandidates.length > 0 || employees.some(employee => employee.account_user_id);

  const load = useCallback(async () => {
    if (!headers['X-Organization-Id']) { setLoading(false); return; }
    setLoading(true);
    try {
      const [eRes, sRes, tRes, stRes, cRes] = await Promise.all([
        fetch(`${API_URL}/api/hrm/employees`, { headers }),
        fetch(`${API_URL}/api/hrm/shifts`, { headers }),
        fetch(`${API_URL}/api/hrm/timeoff`, { headers }),
        fetch(`${API_URL}/api/hrm/stats`, { headers }),
        fetch(`${API_URL}/api/hrm/account-candidates`, { headers }),
      ]);
      setEmployees(eRes.ok ? await eRes.json() : []);
      setShifts(sRes.ok ? await sRes.json() : []);
      setTimeoff(tRes.ok ? await tRes.json() : []);
      setStats(stRes.ok ? await stRes.json() : null);
      setAccountCandidates(cRes.ok ? await cRes.json() : []);
    } finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const saveEmployee = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      const body = clean({ ...empForm, hourly_rate: parseFloat(empForm.hourly_rate) || 0 });
      const res = await fetch(`${API_URL}/api/hrm/employees`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      if (res.ok) { setEmpForm(null); load(); }
      else {
        const err = await res.json().catch(() => ({}));
        setFormError(typeof err.detail === 'string' ? err.detail : 'Could not create employee. Check the fields.');
      }
    } catch { setFormError('Network error. Please try again.'); }
    finally { setSaving(false); }
  };

  const saveShift = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      const res = await fetch(`${API_URL}/api/hrm/shifts`, {
        method: 'POST', headers, body: JSON.stringify(clean(shiftForm)),
      });
      if (res.ok) { setShiftForm(null); load(); }
      else {
        const err = await res.json().catch(() => ({}));
        setFormError(typeof err.detail === 'string' ? err.detail : 'Could not create shift.');
      }
    } catch { setFormError('Network error. Please try again.'); }
    finally { setSaving(false); }
  };

  const saveTimeoff = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      const res = await fetch(`${API_URL}/api/hrm/timeoff`, {
        method: 'POST', headers, body: JSON.stringify(clean(timeoffForm)),
      });
      if (res.ok) { setTimeoffForm(null); load(); }
      else {
        const err = await res.json().catch(() => ({}));
        setFormError(typeof err.detail === 'string' ? err.detail : 'Could not create request.');
      }
    } catch { setFormError('Network error. Please try again.'); }
    finally { setSaving(false); }
  };

  const decideTimeoff = async (id, decision) => {
    const res = await fetch(`${API_URL}/api/hrm/timeoff/${id}/decision?decision=${decision}`, { method: 'PUT', headers });
    if (res.ok) load();
  };

  const changeEmployeeAccount = async (employee, unlink = false) => {
    setSaving(true);
    setFormError(null);
    try {
      const res = await fetch(`${API_URL}/api/hrm/employees/${encodeURIComponent(employee.id)}/account`, {
        method: unlink ? 'DELETE' : 'PUT',
        headers,
        ...(unlink ? {} : { body: JSON.stringify({ user_id: accountSelections[employee.id] || '' }) }),
      });
      if (res.ok) await load();
      else {
        const err = await res.json().catch(() => ({}));
        setFormError(typeof err.detail === 'string' ? err.detail : 'Could not update employee account link.');
      }
    } catch { setFormError('Network error. Please try again.'); }
    finally { setSaving(false); }
  };

  if (!orgId && !loading) {
    return <div className="max-w-6xl mx-auto p-8 text-ink-muted" data-testid="hrm-no-org">No active organization. Please log in.</div>;
  }

  return (
    <div className="min-h-screen bg-surface-muted py-8" data-testid="hrm-page">
      <div className="max-w-6xl mx-auto px-4">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-overline text-accent-500 mb-1">People & Scheduling</div>
          <h1 className="font-display text-3xl text-ink">HR & Rota</h1>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <StatCard label="Headcount" value={stats.active_headcount} icon={Users} />
            <StatCard label="Shifts This Week" value={stats.shifts_this_week} icon={CalendarClock} />
            <StatCard label="Labour Cost/Wk" value={`£${(stats.projected_labour_cost_this_week || 0).toFixed(0)}`} icon={PoundSterling} />
            <StatCard label="Pending Time-off" value={stats.pending_timeoff} icon={Plane} />
          </div>
        )}

        <div className="flex gap-2 mb-4 border-b border-surface-border">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`hrm-tab-${t.toLowerCase().replace(/[^a-z]/g, '')}`}
              className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 motion-organic ${tab === t ? 'border-primary-500 text-primary-700' : 'border-transparent text-ink-muted hover:text-ink'}`}>
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-500" /></div>
        ) : (
          <>
            {tab === 'Team' && (
              <div>
                {formError && <div className="mb-3 bg-danger/10 text-danger text-sm rounded-md px-3 py-2" role="alert">{formError}</div>}
                <div className="flex justify-end mb-3">
                  <button onClick={() => { setFormError(null); setEmpForm({ name: '', email: '', phone: '', position: '', department: '', employment_type: 'full_time', hourly_rate: '', start_date: '' }); }}
                    data-testid="hrm-add-employee-btn"
                    className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-600">
                    <UserPlus className="w-4 h-4" /> Add Employee
                  </button>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-surface-border overflow-hidden">
                  {employees.length === 0 ? (
                    <div className="p-10 text-center text-ink-muted" data-testid="hrm-team-empty">No employees yet.</div>
                  ) : (
                    <table className="w-full text-sm" data-testid="hrm-employees-table">
                      <thead className="bg-surface-sunken text-ink-muted text-xs uppercase">
                        <tr>
                          <th className="text-left px-4 py-3">Name</th>
                          <th className="text-left px-4 py-3">Position</th>
                          <th className="text-left px-4 py-3">Type</th>
                          <th className="text-right px-4 py-3">Rate/hr</th>
                          <th className="text-left px-4 py-3">Status</th>
                          {showAccountControls && <th className="text-left px-4 py-3">Platform account</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {employees.map(emp => (
                          <tr key={emp.id} className="border-t border-surface-border" data-testid={`hrm-emp-${emp.id}`}>
                            <td className="px-4 py-3 font-medium text-ink">{emp.name}</td>
                            <td className="px-4 py-3 text-ink-muted">{emp.position || '—'} {emp.department ? `· ${emp.department}` : ''}</td>
                            <td className="px-4 py-3 text-ink-muted">{(emp.employment_type || '').replace('_', ' ')}</td>
                            <td className="px-4 py-3 text-right tabular-nums">£{(emp.hourly_rate || 0).toFixed(2)}</td>
                            <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${emp.status === 'active' ? 'bg-ok/10 text-ok' : 'bg-surface-sunken text-ink-muted'}`}>{emp.status}</span></td>
                            {showAccountControls && <td className="px-4 py-3">
                              {emp.location_id && emp.location_id !== locationId ? <span className="text-xs text-ink-muted">Managed at another site</span> : <>
                              <div className="flex items-center gap-2 min-w-64">
                                <select
                                  aria-label={`Account for ${emp.name}`}
                                  value={accountSelections[emp.id] ?? emp.account_user_id ?? ''}
                                  onChange={e => setAccountSelections({ ...accountSelections, [emp.id]: e.target.value })}
                                  className="min-w-0 flex-1 px-2 py-2 border border-surface-border rounded-md text-sm"
                                >
                                  <option value="">Choose member</option>
                                  {accountCandidates.map(member => <option key={member.user_id} value={member.user_id}>{member.name}</option>)}
                                </select>
                                <button
                                  type="button"
                                  aria-label={`Link account to ${emp.name}`}
                                  title="Link account"
                                  disabled={saving || !accountSelections[emp.id] || accountSelections[emp.id] === emp.account_user_id}
                                  onClick={() => changeEmployeeAccount(emp)}
                                  className="p-2 border border-surface-border rounded-md disabled:opacity-40"
                                ><Link2 className="w-4 h-4" /></button>
                                {emp.account_user_id && <button
                                  type="button"
                                  aria-label={`Unlink account from ${emp.name}`}
                                  title="Unlink account"
                                  disabled={saving}
                                  onClick={() => changeEmployeeAccount(emp, true)}
                                  className="p-2 border border-surface-border rounded-md"
                                ><Unlink className="w-4 h-4" /></button>}
                              </div>
                              {emp.account_user_id && <span className="block mt-1 text-xs text-ink-muted">Linked to {accountCandidates.find(m => m.user_id === emp.account_user_id)?.name || emp.account_user_id}</span>}
                              </>}
                            </td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {tab === 'Rota' && (
              <div>
                <div className="flex justify-end mb-3">
                  <button onClick={() => { setFormError(null); setShiftForm({ employee_id: employees[0]?.id || '', start: '', end: '', role: '' }); }}
                    disabled={employees.length === 0} data-testid="hrm-add-shift-btn"
                    className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-50">
                    <CalendarClock className="w-4 h-4" /> Add Shift
                  </button>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-surface-border overflow-hidden">
                  {shifts.length === 0 ? (
                    <div className="p-10 text-center text-ink-muted" data-testid="hrm-rota-empty">No shifts scheduled. {employees.length === 0 && 'Add an employee first.'}</div>
                  ) : (
                    <table className="w-full text-sm" data-testid="hrm-shifts-table">
                      <thead className="bg-surface-sunken text-ink-muted text-xs uppercase">
                        <tr>
                          <th className="text-left px-4 py-3">Employee</th>
                          <th className="text-left px-4 py-3">Start</th>
                          <th className="text-left px-4 py-3">End</th>
                          <th className="text-right px-4 py-3">Hours</th>
                          <th className="text-right px-4 py-3">Cost</th>
                          <th className="text-left px-4 py-3">Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shifts.map(s => (
                          <tr key={s.id} className="border-t border-surface-border" data-testid={`hrm-shift-${s.id}`}>
                            <td className="px-4 py-3 font-medium text-ink">{s.employee_name || s.employee_id}</td>
                            <td className="px-4 py-3 text-ink-muted">{new Date(s.start).toLocaleString()}</td>
                            <td className="px-4 py-3 text-ink-muted">{new Date(s.end).toLocaleString()}</td>
                            <td className="px-4 py-3 text-right tabular-nums">{s.hours}</td>
                            <td className="px-4 py-3 text-right tabular-nums">£{(s.projected_cost || 0).toFixed(2)}</td>
                            <td className="px-4 py-3 text-ink-muted">{s.role || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}

            {tab === 'Time-off' && (
              <div>
                <div className="flex justify-end mb-3">
                  <button onClick={() => { setFormError(null); setTimeoffForm({ employee_id: employees[0]?.id || '', start_date: '', end_date: '', reason: '' }); }}
                    disabled={employees.length === 0} data-testid="hrm-add-timeoff-btn"
                    className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-50">
                    <Plane className="w-4 h-4" /> Request Time-off
                  </button>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-surface-border overflow-hidden">
                {timeoff.length === 0 ? (
                  <div className="p-10 text-center text-ink-muted" data-testid="hrm-timeoff-empty">No time-off requests.</div>
                ) : (
                  <table className="w-full text-sm" data-testid="hrm-timeoff-table">
                    <thead className="bg-surface-sunken text-ink-muted text-xs uppercase">
                      <tr>
                        <th className="text-left px-4 py-3">Employee</th>
                        <th className="text-left px-4 py-3">From</th>
                        <th className="text-left px-4 py-3">To</th>
                        <th className="text-left px-4 py-3">Reason</th>
                        <th className="text-left px-4 py-3">Status</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {timeoff.map(r => (
                        <tr key={r.id} className="border-t border-surface-border" data-testid={`hrm-timeoff-${r.id}`}>
                          <td className="px-4 py-3 font-medium text-ink">{r.employee_name || r.employee_id}</td>
                          <td className="px-4 py-3 text-ink-muted">{r.start_date}</td>
                          <td className="px-4 py-3 text-ink-muted">{r.end_date}</td>
                          <td className="px-4 py-3 text-ink-muted">{r.reason || '—'}</td>
                          <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded ${r.status === 'approved' ? 'bg-ok/10 text-ok' : r.status === 'rejected' ? 'bg-danger/10 text-danger' : 'bg-warn/10 text-warn'}`}>{r.status}</span></td>
                          <td className="px-4 py-3 text-right">
                            {r.status === 'pending' && (
                              <div className="flex gap-2 justify-end">
                                <button onClick={() => decideTimeoff(r.id, 'approved')} data-testid={`hrm-approve-${r.id}`} className="text-ok hover:bg-ok/10 p-1 rounded"><Check className="w-4 h-4" /></button>
                                <button onClick={() => decideTimeoff(r.id, 'rejected')} data-testid={`hrm-reject-${r.id}`} className="text-danger hover:bg-danger/10 p-1 rounded"><Ban className="w-4 h-4" /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Employee modal */}
      {empForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="hrm-emp-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl text-ink">New Employee</h2>
              <button onClick={() => setEmpForm(null)}><X className="w-5 h-5 text-ink-muted" /></button>
            </div>
            <form onSubmit={saveEmployee} className="space-y-3">
              {formError && <div className="bg-danger/10 text-danger text-sm rounded-md px-3 py-2" data-testid="hrm-emp-error">{formError}</div>}
              <input required placeholder="Full name" value={empForm.name} onChange={e => setEmpForm({ ...empForm, name: e.target.value })} data-testid="hrm-emp-name" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Position" value={empForm.position} onChange={e => setEmpForm({ ...empForm, position: e.target.value })} data-testid="hrm-emp-position" className="px-3 py-2 border border-surface-border rounded-md text-sm" />
                <input placeholder="Department" value={empForm.department} onChange={e => setEmpForm({ ...empForm, department: e.target.value })} data-testid="hrm-emp-dept" className="px-3 py-2 border border-surface-border rounded-md text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <select value={empForm.employment_type} onChange={e => setEmpForm({ ...empForm, employment_type: e.target.value })} data-testid="hrm-emp-type" className="px-3 py-2 border border-surface-border rounded-md text-sm">
                  {['full_time', 'part_time', 'casual', 'contractor'].map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                </select>
                <input type="number" step="0.01" placeholder="Rate/hr (£)" value={empForm.hourly_rate} onChange={e => setEmpForm({ ...empForm, hourly_rate: e.target.value })} data-testid="hrm-emp-rate" className="px-3 py-2 border border-surface-border rounded-md text-sm" />
              </div>
              <input type="email" placeholder="Email" value={empForm.email} onChange={e => setEmpForm({ ...empForm, email: e.target.value })} data-testid="hrm-emp-email" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" />
              <button type="submit" disabled={saving} data-testid="hrm-emp-submit" className="w-full bg-primary-500 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-50">{saving ? 'Saving…' : 'Create Employee'}</button>
            </form>
          </div>
        </div>
      )}

      {/* Shift modal */}
      {shiftForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="hrm-shift-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl text-ink">New Shift</h2>
              <button onClick={() => setShiftForm(null)}><X className="w-5 h-5 text-ink-muted" /></button>
            </div>
            <form onSubmit={saveShift} className="space-y-3">
              {formError && <div className="bg-danger/10 text-danger text-sm rounded-md px-3 py-2" data-testid="hrm-shift-error">{formError}</div>}
              <select required value={shiftForm.employee_id} onChange={e => setShiftForm({ ...shiftForm, employee_id: e.target.value })} data-testid="hrm-shift-employee" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm">
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
              <label className="block text-xs text-ink-muted">Start<input required type="datetime-local" value={shiftForm.start} onChange={e => setShiftForm({ ...shiftForm, start: e.target.value })} data-testid="hrm-shift-start" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm mt-1" /></label>
              <label className="block text-xs text-ink-muted">End<input required type="datetime-local" value={shiftForm.end} onChange={e => setShiftForm({ ...shiftForm, end: e.target.value })} data-testid="hrm-shift-end" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm mt-1" /></label>
              <input placeholder="Role (e.g. Line cook)" value={shiftForm.role} onChange={e => setShiftForm({ ...shiftForm, role: e.target.value })} data-testid="hrm-shift-role" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" />
              <button type="submit" disabled={saving} data-testid="hrm-shift-submit" className="w-full bg-primary-500 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-50">{saving ? 'Saving…' : 'Create Shift'}</button>
            </form>
          </div>
        </div>
      )}

      {/* Time-off modal */}
      {timeoffForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="hrm-timeoff-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl text-ink">Request Time-off</h2>
              <button onClick={() => setTimeoffForm(null)}><X className="w-5 h-5 text-ink-muted" /></button>
            </div>
            <form onSubmit={saveTimeoff} className="space-y-3">
              {formError && <div className="bg-danger/10 text-danger text-sm rounded-md px-3 py-2" data-testid="hrm-timeoff-error">{formError}</div>}
              <select required value={timeoffForm.employee_id} onChange={e => setTimeoffForm({ ...timeoffForm, employee_id: e.target.value })} data-testid="hrm-timeoff-employee" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm">
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
              <label className="block text-xs text-ink-muted">From<input required type="date" value={timeoffForm.start_date} onChange={e => setTimeoffForm({ ...timeoffForm, start_date: e.target.value })} data-testid="hrm-timeoff-start" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm mt-1" /></label>
              <label className="block text-xs text-ink-muted">To<input required type="date" value={timeoffForm.end_date} onChange={e => setTimeoffForm({ ...timeoffForm, end_date: e.target.value })} data-testid="hrm-timeoff-end" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm mt-1" /></label>
              <input placeholder="Reason" value={timeoffForm.reason} onChange={e => setTimeoffForm({ ...timeoffForm, reason: e.target.value })} data-testid="hrm-timeoff-reason" className="w-full px-3 py-2 border border-surface-border rounded-md text-sm" />
              <button type="submit" disabled={saving} data-testid="hrm-timeoff-submit" className="w-full bg-primary-500 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-50">{saving ? 'Saving…' : 'Submit Request'}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRM;
